// Free gems (room 1's offers and claims; the accounts area), through the doorway (doorway.ts): the earn-gems page,
// the daily-gems prompt and the four welcome-back callers. Each call sends exactly what its screen sent before it
// moved here (tests/gems-api.test.mjs), and with the accounts area's switch on is answered by v2 in v1's shape
// (gems-v2.ts). v2 decides who qualifies and how much; each claim on v2 carries a receipt number made for that press.
//
// While v2 lacks what they need (doorway.ts, `V2_LACKS`), they keep to v1, the switch whatever: moving free gems
// (`gemClaims`), and for the earn-gems screen, whose daily claim pays by the visitor's country, the country
// (`country`); both false since 23 October 2026. The screen's state, its task starts and its claims move together, so
// one screen never reads one backend's tasks and daily claim and claims them on the other's.
import {
  call,
  lazySide,
  signedIn,
  signedInJson,
  unlessV2Lacks,
} from './doorway.ts'
import type { V2Lack, V2Side } from './doorway.ts'

const AREA = 'accounts'

/** What the earn-gems screen (its state, the daily claim, the tasks) and the daily-gems prompt need of v2. */
const EARN_GEMS: readonly V2Lack[] = ['country', 'gemClaims']
/** What welcome back's claim needs of v2: it pays the same everywhere. */
const WELCOME_BACK: readonly V2Lack[] = ['gemClaims']

type Sides = typeof import('./gems-v2.ts')

/**
 * A call's v2 side, as `use` builds it from gems-v2.ts, loaded only when the switch puts the visitor on v2. When that
 * module fails to load, the call goes to v1 at `path`, as it would have with the switch off.
 */
const onV2 = (path: string, use: (loaded: Sides) => V2Side): V2Side =>
  lazySide(() => import('./gems-v2.ts'), use, path)

/**
 * The earn-gems screen's state: `{ gems, daily, tasks }` (`daily` null when the daily claim isn't offered). The
 * daily-gems prompt reads it too, signed in without a content type, as it always has.
 */
export const earnGemsStatus = (
  token: string,
  { json = true }: { json?: boolean } = {},
) =>
  call(
    AREA,
    '/api/earn_gems_status',
    { headers: json ? signedInJson(token) : signedIn(token) },
    unlessV2Lacks(EARN_GEMS, {
      token,
      side: onV2('/api/earn_gems_status', (loaded) => loaded.status),
    }),
  )

/** Claims today's daily gems: `{ gems, gems_amount, daily }`; a refusal answers `{ error }`. */
export const claimDailyReward = (token: string) =>
  call(
    AREA,
    '/api/claim_daily_reward',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({}),
    },
    unlessV2Lacks(EARN_GEMS, {
      token,
      side: onV2('/api/claim_daily_reward', (loaded) => loaded.claimDaily),
    }),
  )

/** Starts a task's clock before its link opens: `{ started_at }` in seconds since 1970. */
export const startTask = (token: string, taskId: string) =>
  call(
    AREA,
    '/api/start_task',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ task_id: taskId }),
    },
    unlessV2Lacks(EARN_GEMS, {
      token,
      side: onV2(
        '/api/start_task',
        (loaded) => (t, init) => loaded.startTask(t, init, taskId),
      ),
    }),
  )

/** Claims a task's gems after its wait: `{ gems, gems_amount }`. */
export const completeTask = (token: string, taskId: string) =>
  call(
    AREA,
    '/api/complete_task',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ task_id: taskId }),
    },
    unlessV2Lacks(EARN_GEMS, {
      token,
      side: onV2(
        '/api/complete_task',
        (loaded) => (t, init) => loaded.claimTask(t, init, taskId),
      ),
    }),
  )

/** Welcome back's gems, once per account: `{ gems }`; claimed already answers 403 `{ error }`. */
export const claimWelcomeReward = (token: string) =>
  call(
    AREA,
    '/api/claim_welcome_reward',
    {
      method: 'POST',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
    },
    unlessV2Lacks(WELCOME_BACK, {
      token,
      side: onV2('/api/claim_welcome_reward', (loaded) => loaded.claimWelcome),
    }),
  )
